<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { format } from 'date-fns'
import PackageCard from './PackageCard.vue'
import VideoPlayer from './VideoPlayer.vue'
import { themes } from '@/types'
import type { HotspotConfig, HotspotProps, Package } from '@/types'
import { Button } from '@/components/ui/button'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { cn, formatDataUnitSize, formatPhoneNumber } from '@/lib/utils'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import Tawk from '@/components/Tawk.vue'

const props = defineProps<HotspotProps>()

const buyDisabled = ref(false)
const connectDisabled = ref(true)
const priceAscending = ref(false)

const submitted = ref(false)
const processing = ref(false)
const processingText = ref('')
const success = ref(false)
const root = ref<HTMLElement>()
const errMessage = ref<string | Uint8Array>()
const isLandscape = ref(false)
const config = ref<HotspotConfig | null>(null)

// const packages = ref<Package[]>([])
const fetching = ref(true)
const options = ref<string[]>([])

const username = ref ('')
const password = ref('')

const packagesRef = ref<Package[]>()
const currentSelectedVideo = ref()

// Add these new refs to the existing refs section
const voucherCode = ref('')
const voucherProcessing = ref(false)
const voucherSuccess = ref(false)
const voucherError = ref(false)
const voucherErrorMessage = ref('')

// Add this function to handle voucher submission
async function handleVoucherSubmit(e) {
  e.preventDefault()
  voucherProcessing.value = true
  voucherError.value = false
  voucherErrorMessage.value = ''

  const token = `hotspot.${props.org}`

  // Get MAC address from macInput if available
  const macInput = document.getElementById('macInput') as HTMLInputElement || { value: '' }

  try {
    const response = await fetch(`${props.gatewayBaseUrl}/backend/api/hotspot.connect-voucher?mac=${macInput.value}&routerId=${props.router}&voucher=${voucherCode.value.toUpperCase()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    })

    const result = await response.json()

    if (response.ok) {
      voucherSuccess.value = true

      if (result.username && result.password) {
        // Store the credentials for login
        localStorage.setItem('creds', JSON.stringify({
          username: result.username,
          password: result.password,
        }))

        username.value = result.username
        password.value = result.password

        // Store expiry information if provided
        if (result.purchasableExpiry) {
          localStorage.setItem('expiry_trial', JSON.stringify({
            expiry: result.purchasableExpiry,
            trial: 2,
          }))
        }

        // Execute login
        handleMikrotikLogin()
      }
      else {
        voucherErrorMessage.value = 'Invalid credentials received from server'
        voucherError.value = true
      }
    }
    else {
      voucherError.value = true
      voucherErrorMessage.value = result.error?.message || 'Failed to connect with voucher'
    }
  }
  catch (error: any) {
    console.error('Voucher connection error:', error)
    voucherError.value = true
    voucherErrorMessage.value = error.message || 'Error connecting with voucher'
  }
  finally {
    voucherProcessing.value = false
  }
}

// Add this function to reset voucher state
function resetVoucherState() {
  voucherCode.value = ''
  voucherProcessing.value = false
  voucherSuccess.value = false
  voucherError.value = false
  voucherErrorMessage.value = ''
}

const purchaseState = ref<{
  transactionId: null | string
  packageId: null | string
  packageName: null | string
  amount: null | string
  paymentMethod: null | string
  mac: null | string
  status: null | 'processing' | 'payment-successful' | 'error' // 'processing', 'payment-successful', 'error'
  timestamp: null | number
  currentSelectedVideo: null | string
}>({
  transactionId: null,
  packageId: null,
  packageName: null,
  amount: null,
  paymentMethod: null,
  mac: null,
  status: null, // 'processing', 'payment-successful', 'error'
  timestamp: null,
  currentSelectedVideo: null,
})

// Function to save the current purchase state to localStorage
function savePurchaseState(data: {
  transactionId: null | string
  packageId: null | string
  packageName: null | string
  amount: null | string
  paymentMethod: null | string
  mac: null | string
  status: null | 'processing' | 'payment-successful' | 'error' // 'processing', 'payment-successful', 'error'
  timestamp: null | number
  currentSelectedVideo: null | string
}) {
  purchaseState.value = data
  localStorage.setItem('purchase_state', JSON.stringify({
    ...data,
    timestamp: Date.now(),
  }))
}

// Function to clear the purchase state
function clearPurchaseState() {
  localStorage.removeItem('purchase_state')
  purchaseState.value = {
    transactionId: null,
    packageId: null,
    packageName: null,
    amount: null,
    paymentMethod: null,
    mac: null,
    status: null,
    timestamp: null,
    currentSelectedVideo: null,
  }
}

function isImageSrc(src: string) {
  return src.startsWith('http') || src.startsWith('data:image')
}

function handleClose(open: boolean) {
  if (open === false)
    connectDisabled.value = true
}

// Open a modal that states(Trying to reconnect you!)
function handleMikrotikLogin() {
  // retries login after every second until timeout(20s) then involve verify route with transactionId and if purchaseGranted is false and transaction success it creates a user
  // in the case that a transaction is granted and the expiry is not reached we create a hotspot user with (expiry - now)
  // if transaction was granted and expiry was reached just remove this login credentials
  // Tell the user to buy another package
  const mikrotikForm = document.getElementById('loginForm') as HTMLFormElement

  const usernameInput = document.getElementById('usernameInput') as HTMLInputElement
  usernameInput.value = username.value
  const passowrdInput = document.getElementById('passwordInput') as HTMLInputElement
  passowrdInput.value = password.value

  mikrotikForm!.submit()
}

function formatTime(time: string): string {
  const unit = time.slice(-1).toLowerCase()
  const value = Number.parseInt(time.slice(0, -1))

  switch (unit) {
    case 'h':
      if (value > 48 && value % 24 === 0)
        return `${Math.round(value / 24)} Days`

      return value === 1 ? `${value} Hour` : `${value} Hours`
    case 'm':
      return value === 1 ? `${value} Minute` : `${value} Minutes`
    case 'd':
      return value === 1 ? `${value} Day` : `${value} Days`
    default:
      return time
  }
}

async function handlePurchase(e: SubmitEvent) {
  e.preventDefault()
  buyDisabled.value = true
  const token = `hotspot.${props.org}`
  processingText.value = 'We are sending you a push'
  submitted.value = true
  processing.value = true

  const formData = new FormData(e.target as HTMLFormElement)
  const macInput = document.getElementById('macInput') as HTMLInputElement || { value: '' }

  const paymentMethod = formData.get('payment_method')

  const body = {
    phoneNumber: paymentMethod === 'mpesa' ? formatPhoneNumber(formData.get('phone') as string) : `250${formData.get('phone')}`,
    packageId: formData.get('package'),
    packageName: formData.get('package_name'),
    amount: formData.get('amount'),
    paymentMethod,
    mac: macInput.value,
    routerId: props.router,
    adUrl: paymentMethod === 'ad' ? currentSelectedVideo.value : undefined,
  }

  if (props.gatewayBaseUrl) {
    // handle package purchase
    try {
      const value = await fetch(`${props.gatewayBaseUrl}/backend/api/hotspot.purchase`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      const responseP = await value.json()

      if (!responseP.error) {
        // Save initial state
        savePurchaseState({
          transactionId: responseP.id as string,
          packageId: body.packageId as string,
          packageName: body.packageName as string,
          amount: body.amount as string,
          paymentMethod: paymentMethod as string,
          mac: macInput.value,
          status: 'processing',
          timestamp: Date.now(),
          currentSelectedVideo: paymentMethod === 'ad' ? currentSelectedVideo.value : null,
        })

        processingText.value = paymentMethod === 'mpesa' ? 'STK push sent, enter PIN' : 'Sent you a push, enter PIN'
        // Store credentials for use later
        localStorage.setItem('creds', JSON.stringify({ username: responseP.username, password: responseP.password }))
        // check if credentials exist after setting and are not removed by a race condition, if not set again
        const credsExistAfterSetting = JSON.parse(localStorage.getItem("creds") || "{}")
        if(typeof credsExistAfterSetting === 'object' && Object.keys(credsExistAfterSetting).length > 0 )
          localStorage.setItem('creds', JSON.stringify({ username: responseP.username, password: responseP.password }))

        // Flag to track if transaction is already processed
        let isProcessed = false
        const interval = setInterval(async () => {
          // Skip if already processed
          if (isProcessed) {
            clearInterval(interval)
            return
          }

          try {
            const value = await fetch(`${props.gatewayBaseUrl}/backend/api/hotspot.check-purchase?id=${responseP.id}`, {
              method: 'GET',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
            })

            if (value.ok) {
              const response = await value.json()

              if (response.paymentState === 'PROCESSING') {
              // continue polling
                return
              }

              // Mark as processed immediately to prevent multiple executions
              isProcessed = true
              clearInterval(interval)

              if (response.paymentState) {
                if (response.paymentState === 'SUCCESSFUL' && processingText.value !== 'Purchase successful, please wait as we connect you') {
                  processingText.value = 'Purchase successful, please wait as we connect you'

                  // Update purchase state
                  purchaseState.value = { ...purchaseState.value ,status: 'payment-successful'}
                  savePurchaseState(purchaseState.value)

                  try {
                    const connectUrl = `${props.gatewayBaseUrl}/backend/api/hotspot.connect?routerId=${props.router}&id=${response.id}&username=${responseP.username}&password=${responseP.password}&mac=${macInput.value}`

                    const value = await fetch(connectUrl, {
                      method: 'GET',
                      headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                      },
                    })

                    const res = await value.json()

                    if (value.ok) {
                      processing.value = false
                      success.value = true
                      buyDisabled.value = false
                      username.value = responseP.username
                      password.value = responseP.password
                      localStorage.setItem('expiry_trial', JSON.stringify({ expiry: res.purchasableExpiry, trial: 2 }))

                      // Clear purchase state after successful connection
                      clearPurchaseState()

                      handleMikrotikLogin()
                    }
                    else {
                      clearPurchaseState()
                    }
                  }
                  catch (error) {
                    console.error('Connection error:', error)
                    processing.value = false
                    success.value = false
                    buyDisabled.value = false
                    errMessage.value = 'Error connecting to the internet'

                    clearPurchaseState()
                  }
                }
                else {
                  if (response.paymentState !== 'SUCCESSFUL') {
                    localStorage.removeItem('creds')
                    processing.value = false
                    success.value = false
                    buyDisabled.value = false
                    errMessage.value = response.paymentState === 'FAILED'
                      ? 'Transaction Failed'
                      : response.paymentState === 'CANCELLED'
                        ? 'Transaction Cancelled'
                        : 'An Error Occurred'

                    clearPurchaseState()
                  }
                }
              }
            }
          }
          catch (error) {
            console.error('Check purchase error:', error)
            isProcessed = true
            clearInterval(interval)
            processing.value = false
            buyDisabled.value = false
            errMessage.value = 'Error checking transaction status'

            clearPurchaseState()
          }
        }, 1000)
      }
      else {
        processing.value = false
        success.value = false
        errMessage.value = responseP.error.message.includes('You have exhausted your free data, try again at ')
          ? `You have exhausted your free data, try again at ${format(Number(responseP.error.message.replace('You have exhausted your free data, try again at ', '')), 'h:mm a MMM dd yyyy')}`
          : responseP.error.message
        buyDisabled.value = false

        clearPurchaseState()
      }
    }
    catch (e: any) {
      processing.value = false
      buyDisabled.value = false
      errMessage.value = e.message

      clearPurchaseState()
    }
  }
  else {
    processing.value = false
    buyDisabled.value = false
    errMessage.value = 'It\'s not you it\'s us'
    throw new Error('packagePurchaseUrl not set')

    clearPurchaseState()
  }
}

function sortPriceAsc() {
  // sort by ascending price
  packagesRef.value = packagesRef.value!.sort((a, b) => {
    const priceA = a.price || 0
    const priceB = b.price || 0
    return priceA - priceB
  })
  priceAscending.value = true
}

function sortPriceDesc() {
  // sort by descending price
  packagesRef.value = packagesRef.value!.sort((a, b) => {
    const priceA = a.price || 0
    const priceB = b.price || 0
    return priceB - priceA
  })
  priceAscending.value = false
}

// Function to resume from stored state
async function resumePurchaseState() {
  const token = `hotspot.${props.org}`
  const storedState = localStorage.getItem('purchase_state')

  if (!storedState)
    return

  const state = JSON.parse(storedState)
  purchaseState.value = state

  // Check if state is too old (30 minutes)
  const MAX_STATE_AGE = 30 * 60 * 1000 // 30 minutes
  if (Date.now() - state.timestamp > MAX_STATE_AGE) {
    clearPurchaseState()
    return
  }

  // Set UI based on state
  submitted.value = true

  // Handle different states
  if (state.status === 'processing') {
    processing.value = true
    buyDisabled.value = true
    const paymentMethod = state.paymentMethod
    processingText.value = paymentMethod === 'mpesa' ? 'Package purchase resumed, checking status' : 'Package purchase resumed, checking status'

    // Resume checking transaction status
    if (state.transactionId) {
      let isProcessed = false
      const interval = setInterval(async () => {
        if (isProcessed) {
          clearInterval(interval)
          return
        }

        try {
          const value = await fetch(`${props.gatewayBaseUrl}/backend/api/hotspot.check-purchase?id=${state.transactionId}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          })

          const response = await value.json()

          if (response.paymentState === 'PROCESSING')
            return

          isProcessed = true
          clearInterval(interval)

          if (response.paymentState === 'SUCCESSFUL' && processingText.value !== 'Purchase successful, please wait as we connect you') {
            processingText.value = 'Purchase successful, please wait as we connect you'
            const credentials = JSON.parse(localStorage.getItem('creds')!)

            // Update purchase state
            purchaseState.value = { ...purchaseState.value ,status: 'payment-successful'}
            savePurchaseState(purchaseState.value)

            try {
              const connectUrl = `${props.gatewayBaseUrl}/backend/api/hotspot.connect?routerId=${props.router}&id=${state.transactionId}&username=${credentials.username}&password=${credentials.password}&mac=${state.mac}${state.paymentMethod === 'ad' ? `&adUrl=${state.currentSelectedVideo}` : ''}`

              const value = await fetch(connectUrl, {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              })

              const res = await value.json()

              if (value.ok) {
                processing.value = false
                success.value = true
                buyDisabled.value = false
                username.value = credentials.username
                password.value = credentials.password
                localStorage.setItem('expiry_trial', JSON.stringify({ expiry: res.purchasableExpiry, trial: 2 }))

                // Clear purchase state after successful connection
                clearPurchaseState()

                handleMikrotikLogin()
              }
              else {
                clearPurchaseState()
              }
            }
            catch (error) {
              console.error('Connection error:', error)
              processing.value = false
              success.value = false
              buyDisabled.value = false
              errMessage.value = 'Error connecting to the internet'

              clearPurchaseState()
            }
          }
          else {
            localStorage.removeItem('creds')
            processing.value = false
            success.value = false
            buyDisabled.value = false
            errMessage.value = response.paymentState === 'FAILED'
              ? 'Transaction Failed'
              : response.paymentState === 'CANCELLED'
                ? 'Transaction Cancelled'
                : 'An Error Occurred'

            clearPurchaseState()
          }
        }
        catch (error) {
          console.error('Check purchase error:', error)
          isProcessed = true
          clearInterval(interval)
          processing.value = false
          buyDisabled.value = false
          errMessage.value = 'Error checking transaction status'

          clearPurchaseState()
        }
      }, 1000)
    }
  }
  else if (state.status === 'payment-successful') {
    processing.value = true
    buyDisabled.value = true
    processingText.value = 'Purchase successful, please wait as we connect you'
    const credentials = JSON.parse(localStorage.getItem('creds')!)

    try {
      const connectUrl = `${props.gatewayBaseUrl}/backend/api/hotspot.connect?routerId=${props.router}&id=${state.transactionId}&username=${credentials.username}&password=${credentials.password}&mac=${state.mac}${state.paymentMethod === 'ad' ? `&adUrl=${state.currentSelectedVideo}` : ''}`

      const value = await fetch(connectUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      })

      const res = await value.json()

      if (value.ok) {
        processing.value = false
        success.value = true
        buyDisabled.value = false
        username.value = credentials.username
        password.value = credentials.password
        localStorage.setItem('expiry_trial', JSON.stringify({ expiry: res.purchasableExpiry, trial: 2 }))

        // Clear purchase state after successful connection
        clearPurchaseState()

        handleMikrotikLogin()
      }
      else {
        clearPurchaseState()
      }
    }
    catch (error) {
      console.error('Connection error:', error)
      processing.value = false
      success.value = false
      buyDisabled.value = false
      errMessage.value = 'Error connecting to the internet'

      clearPurchaseState()
    }
  }
}

// Updated retry function to clear state first
function retry() {
  clearPurchaseState()
  submitted.value = false
}

async function initializeHotspotPage() {
  const token = `hotspot.${props.org}`
  const hotspotConfig = await fetch(`${props.gatewayBaseUrl}/backend/api/hotspot.get-config?routerId=${props.router}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  }).then(async (value) => { return (await value.json()) as HotspotConfig })

  config.value = hotspotConfig

  if (config.value.dark)
    root.value!.classList.add('dark')
  else
    root.value!.classList.remove('dark')
  if (config.value.theme) {
    root.value!.classList.remove('theme-blue')
    root.value!.classList.add(themes[config.value.theme])
  }

  const paymentOptions: { options: string[] } = await fetch(`${props.gatewayBaseUrl}/backend/api/integrations.get-payment-options?routerId=${props.router}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  }).then(value => value.json())

  options.value = Array.from(new Set(paymentOptions.options))
  const pkgs = await fetch(`${props.gatewayBaseUrl}/backend/api/hotspot_plans.all?routerId=${props.router}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  })
  const paks = await pkgs.json()
  fetching.value = false
  if (paks.length > 0)
    packagesRef.value = paks.map(p => ({ ...p, type: 'PAID' }))

  sortPriceAsc()
}

function handleOrientation() {
  isLandscape.value = window.orientation === 90 || window.orientation === -90
}

// Store random video selections in a Map using package ID as key
const videoSelections = ref(new Map())

function getRandomVideoUrl(pkgId: string, mediaUrls: string) {
  // If we already selected a video for this package, return it
  if (videoSelections.value.has(pkgId))
    return videoSelections.value.get(pkgId)

  // Otherwise, make a new random selection and store it
  const urls = mediaUrls.split(',')
  const randomUrl = urls[Math.floor(Math.random() * urls.length)]
  videoSelections.value.set(pkgId, randomUrl)
  currentSelectedVideo.value = randomUrl
  return randomUrl
}

onUnmounted(() => {
  window.removeEventListener('orientationchange', handleOrientation)
})

onMounted(async () => {
  root.value = document.documentElement
  root.value.classList.add('theme-blue')
  window.addEventListener('orientationchange', handleOrientation)
  handleOrientation()
  // TODO: login if expiry is not reached else delete everything from local Storage
  const expiryTrial = JSON.parse(localStorage.getItem('expiry_trial') ?? '"empty"')
  if (expiryTrial !== 'empty') {
    if (expiryTrial.expiry > Date.now() && expiryTrial.trial !== 0) {
      const credentials = JSON.parse(localStorage.getItem('creds') ?? '"empty"')
      if (credentials !== 'empty') {
        localStorage.setItem('expiry_trial', JSON.stringify({ expiry: expiryTrial.expiry, trial: expiryTrial.trial - 1 }))
        username.value = credentials.username
        password.value = credentials.password
        handleMikrotikLogin()
      }
    }
    else {
      localStorage.removeItem('creds')
      localStorage.removeItem('expiry_trial')
    }
  }

  setTimeout(async () => {
    await initializeHotspotPage()

    resumePurchaseState()
  }, 100)
})
</script>

<template>
  <div v-if="config" class="min-h-screen w-full bg-background">
    <Tawk v-if="config.tawkUrl" :tawk-url="config.tawkUrl" />
    <div v-if="config.background" class="fixed inset-0">
      <img
        :src="config.background"
        alt="Colorful background"
        class="w-full h-full object-cover  brightness-110"
      >
      <div class="absolute inset-0 bg-white/10" />
    </div>
    <div class="w-full  grid grid-rows-[auto_1fr_auto]">
      <div class="w-full z-10 fixed top-0 left-0 flex justify-between items-center px-6 py-4 bg-muted">
        <div class="rounded-b-md flex items-center justify-center">
          <img v-if="!isImageSrc(config.logo || '')" src="../assets/logo.png" style="height: 50px;">
          <img v-else :src="config.logo" style="height: 50px;">
        </div>
        <Dialog>
          <DialogTrigger as-child>
            <Button variant="outline" class="rounded-full text-xs h-auto px-2 py-1 shadow-sm flex gap-2">
              <div class="p-1 border border-primary rounded-full">
                <div class="i-streamline-interface-help-customer-support-2-customer-headphones-headset-help-microphone-phone-person-support text-muted-foreground text-primary" />
              </div>
              <span>Contact Support</span>
            </Button>
          </DialogTrigger>
          <DialogContent class="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Contact Support</DialogTitle>
              <DialogDescription>
                <span v-if="config.contactPhone || config.contactEmail">The following channels can be used to reach out to your admin.</span>
                <span v-else>No support channels have been set up for this business.</span>
              </DialogDescription>
            </DialogHeader>
            <div class="relative min-h-0 flex-1 overflow-y-auto rounded-inherit px-6 pb-6">
              <div p-2 />
              <div v-if="config.contactPhone || config.contactEmail" class="grid grid-rows-2 gap-3 text-sm font-medium">
                <a
                  v-if="config.contactPhone"
                  :href="`tel:${config.contactPhone}`"
                  class="group focus:text-foreground col-span-3 flex flex-col items-center gap-2 border border-border rounded-md pb-4 pt-4.5 transition [&:nth-child(2)]:col-span-3 first:col-span-3 sm:col-span-2 first:bg-purple-900/2 focus:bg-muted hover:bg-muted focus:ring-2 focus:ring-offset-2 focus:ring-primary first:hover:border-purple-900/10 first:hover:bg-purple-900/4 sm:[&:nth-child(2)]:bg-purple-900/2 sm:[&:nth-child(2)]:hover:border-purple-900/10 sm:[&:nth-child(2)]:hover:bg-purple-900/4"
                >
                  <div class="group-hover:text-foreground transform-gpu text-foreground transition">
                    <div i-solar-phone-calling-outline w-full text-4xl text-primary />
                    <span>{{ config.contactPhone }}</span>
                  </div>
                </a>
                <a
                  v-if="config.contactEmail"
                  :href="`mailto:${config.contactEmail}`"
                  class="group focus:text-foreground col-span-3 flex flex-col items-center gap-2 border border-border rounded-md pb-4 pt-4.5 transition [&:nth-child(2)]:col-span-3 first:col-span-3 sm:col-span-2 first:bg-purple-900/2 focus:bg-muted hover:bg-muted focus:ring-2 focus:ring-offset-2 focus:ring-primary first:hover:border-purple-900/10 first:hover:bg-purple-900/4 sm:[&:nth-child(2)]:bg-purple-900/2 sm:[&:nth-child(2)]:hover:border-purple-900/10 sm:[&:nth-child(2)]:hover:bg-purple-900/4"
                >
                  <div class="group-hover:text-foreground transform-gpu text-foreground transition">
                    <div i-solar-letter-linear w-full text-4xl text-primary />
                    <span>{{ config.contactEmail }}</span>
                  </div>
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div class="w-full h-full justify-between  flex flex-col items-center">
        <div class="w-full h-full flex flex-col items-center">
          <Tabs default-value="packages" class="w-full p-6">
            <TabsList class="grid w-full fixed top-82px left-0 grid-cols-2 z-10 rounded-t-0 rounded-b-0">
              <TabsTrigger value="packages">
                Packages
              </TabsTrigger>
              <TabsTrigger value="voucher">
                Voucher
              </TabsTrigger>
            </TabsList>
            <TabsContent value="packages">
              <div class="w-full flex justify-end items-center bg-muted fixed z-10 top-120px left-0 right-0 py-2 px-4">
                <!-- sort by price button -->
                 <Button :disabled="!packagesRef" variant="outline" class="flex items-center gap-2" @click="priceAscending ? sortPriceDesc() : sortPriceAsc()">
                  Sort by price
                  <div :class="cn(priceAscending ? 'i-mdi-sort-numeric-descending' : 'i-mdi-sort-numeric-ascending', 'text-lg')"/>
                 </Button>
              </div>
              <!-- Packages -->
              <div class="grid grid-cols-4 max-lg:grid-cols-3 max-sm:grid-cols-2 gap-2 w-full max-h-full mt-150px">
                <template v-for="pkg in packagesRef" :key="pkg.id">
                  <PackageCard
                    :id="pkg.id"
                    :name="pkg.name"
                    :duration="formatTime(pkg.validity)"
                    :devices="`${pkg.sharedUsers}`"
                    :price="pkg.price?.toString() || '0'"
                    :currency="config?.currency"
                    :quota="pkg.quotaLimit?.split(',')[0] ? formatDataUnitSize(pkg.quotaLimit?.split(',')[0]) : 'Unlimited'"
                    :type="pkg.planType === 'PAID' ? 'PAID' : pkg.planType === 'AD' ? 'AD' : 'FREE'"
                    :handle-dialog-close="handleClose"
                  >
                    <DialogContent
                      v-if="!submitted"
                      class="border-none"
                      :class="cn(isLandscape ? 'p-0 w-100dvw h-full' : 'sm:max-w-[425px]', pkg.planType === 'AD' ? 'p-0 h-[100dvh] max-h-100dvh' : 'max-h-90dvh')"
                    >
                      <template v-if="pkg.planType === 'PAID'">
                        <DialogHeader class="flex flex-col items-start">
                          <DialogTitle>{{ pkg.name }}</DialogTitle>
                          <DialogDescription>
                            {{ pkg.quotaLimit?.split(',')[0] ? formatDataUnitSize(pkg.quotaLimit?.split(',')[0]) : 'Unlimited' }}, {{ formatTime(pkg.validity) }} package for {{ pkg.sharedUsers }} devices at {{ pkg.price }} {{ config.currency !== null ? config.currency : 'KES' }}
                          </DialogDescription>
                        </DialogHeader>
                        <div v-if="options.length === 0">
                          No payment setup. Please contact admin
                        </div>
                        <Tabs v-else :default-value="options[0] === 'kpay_momo' || options[0] === 'mtn' ? 'mtn' : options[0] === 'kpay_card' ? 'card' : options[0]">
                          <TabsList class="grid w-full h-fit sm:rounded-b-sm rounded-[0px] z-1" :class="cn(`grid-cols-${options.length}`)">
                            <TabsTrigger v-for="option, index in options" :key="index" class="w-full" :value="option === 'kpay_momo' || option === 'mtn' ? 'mtn' : option === 'kpay_card' ? 'card' : option">
                              <span class="uppercase">{{ option === 'kpay_momo' || option === 'mtn' ? 'momo' : option === 'kpay_card' ? 'card' : option === 'kpay_airtel' ? 'airtel' : option }}</span>
                            </TabsTrigger>
                          </TabsList>
                          <TabsContent value="mpesa">
                            <form :onsubmit="handlePurchase">
                              <div class="grid gap-4 py-4">
                                <div class="hotspot-input">
                                  <div class="flex flex-col gap-2">
                                    <Label for="phone">
                                      Phone
                                    </Label>
                                    <div class="flex w-full items-center">
                                      <div class="px-2 py-2 bg-accent text-muted-foreground border border-border rounded-s h-40px">
                                        +254
                                      </div>
                                      <input id="phone" type="number" name="phone" class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 rounded-s-[0px]">
                                    </div>
                                  </div>
                                  <input type="hidden" name="package" :value="pkg.id">
                                  <input type="hidden" name="package_name" :value="pkg.name">
                                  <input type="hidden" name="amount" :value="pkg.price">
                                  <input type="hidden" name="payment_method" value="mpesa">
                                </div>
                              </div>
                              <DialogFooter>
                                <Button type="submit" class="w-full" :disabled="buyDisabled">
                                  Pay
                                </Button>
                              </DialogFooter>
                            </form>
                          </TabsContent>
                          <TabsContent value="mtn">
                            <form :onsubmit="handlePurchase">
                              <div class="grid gap-4 py-4">
                                <div class="hotspot-input">
                                  <Label for="phone" class="flex flex-col gap-2">
                                    Phone
                                    <div class="flex w-full items-center">
                                      <div class="px-2 py-2 bg-accent text-muted-foreground border border-border rounded-s h-40px">+250</div>
                                      <Input id="phone" type="number" name="phone" class="rounded-s-[0px]" />
                                    </div>
                                  </Label>
                                  <input type="hidden" name="package" :value="pkg.id">
                                  <input type="hidden" name="package_name" :value="pkg.name">
                                  <input type="hidden" name="amount" :value="pkg.price">
                                  <input type="hidden" name="payment_method" value="kpay_mtn">
                                </div>
                              </div>
                              <DialogFooter>
                                <Button type="submit" class="w-full" :disabled="buyDisabled">
                                  Pay
                                </Button>
                              </DialogFooter>
                            </form>
                          </TabsContent>
                          <TabsContent value="card">
                            <form :onsubmit="handlePurchase">
                              <div class="grid gap-4 py-4">
                                <div class="hotspot-input">
                                  <input type="hidden" name="package" :value="pkg.id">
                                  <input type="hidden" name="package_name" :value="pkg.name">
                                  <input type="hidden" name="amount" :value="pkg.price">
                                  <input type="hidden" name="payment_method" value="kpay_card">
                                </div>
                              </div>
                              <DialogFooter>
                                <Button type="submit" class="w-full bg-primary" :disabled="buyDisabled">
                                  Checkout
                                </Button>
                              </DialogFooter>
                            </form>
                          </TabsContent>
                        </Tabs>
                      </template>

                      <template v-if="pkg.planType === 'AD'">
                        <div class="relative flex flex-col items-stretch justify-center w-full">
                          <VideoPlayer v-model="connectDisabled" :video-url="getRandomVideoUrl(pkg.id, pkg.mediaUrls!).replace('autoplay=true', 'autoplay=false')" />
                          <form class="absolute bottom-20px w-full p-4" :onsubmit="handlePurchase">
                            <div class="grid gap-4 py-4">
                              <div class="hotspot-input">
                                <input type="hidden" name="package" :value="pkg.id">
                                <input type="hidden" name="package_name" :value="pkg.name">
                                <input type="hidden" name="amount" :value="pkg.price">
                                <input type="hidden" name="payment_method" value="ad">
                              </div>
                            </div>
                            <DialogFooter>
                              <Button type="submit" class="w-full bg-primary" :disabled="connectDisabled">
                                Connect
                              </Button>
                            </DialogFooter>
                          </form>
                        </div>
                      </template>

                      <template v-if="pkg.planType === 'FREE TRIAL'">
                        <DialogHeader class="flex flex-col items-start">
                          <DialogTitle>Free Plan</DialogTitle>
                          <DialogDescription>
                            {{ pkg.quotaLimit?.split(',')[0] ? formatDataUnitSize(pkg.quotaLimit?.split(',')[0]) : 'Unlimited' }}  package for {{ pkg.sharedUsers }} device(s) valid for {{ formatTime(pkg.quotaLimit?.split(',')[1] ?? '0d') }}
                          </DialogDescription>
                        </DialogHeader>
                        <form :onsubmit="handlePurchase">
                          <div class="grid gap-4 py-4">
                            <div class="hotspot-input">
                              <input type="hidden" name="package" :value="pkg.id">
                              <input type="hidden" name="package_name" :value="pkg.name">
                              <input type="hidden" name="amount" :value="pkg.price">
                              <input type="hidden" name="payment_method" value="free">
                            </div>
                          </div>
                          <DialogFooter>
                            <Button type="submit" class="w-full bg-primary" :disabled="buyDisabled">
                              Connect
                            </Button>
                          </DialogFooter>
                        </form>
                      </template>
                    </DialogContent>
                    <DialogContent
                      v-else
                      class="sm:max-w-[425px]"
                      @interact-outside.prevent
                      @focus-outside.prevent
                      @pointer-down-outside.prevent
                    >
                      <template v-if="pkg.planType === 'PAID'">
                        <div v-if="processing">
                          <DialogHeader class="flex flex-col items-start">
                            <div class="text-2xl font-400 text-red">
                              Do not reload this page
                            </div>
                            <DialogTitle class="flex gap-3 items-center justify-center sm:justify-start text-orange">
                              <div class="i-eos-icons-loading text-3xl" />
                              {{ processingText === 'Purchase successful, please wait as we connect you' ? 'Connecting' : 'Processing' }}
                            </DialogTitle>
                            <DialogDescription>
                              {{ processingText }}
                            </DialogDescription>
                          </DialogHeader>
                          <div class="font-semibold py-2">
                            Please wait while we process your request
                          </div>
                        </div>
                        <div v-else>
                          <div v-if="success">
                            <DialogHeader class="flex flex-col items-start">
                              <DialogTitle class="text-green flex gap-3 items-center justify-center sm:justify-start">
                                <div class="i-clarity-success-standard-line text-3xl" />
                                Success
                              </DialogTitle>
                              <DialogDescription>
                                Successfully connected
                              </DialogDescription>
                            </DialogHeader>
                            <div class="font-semibold py-2">
                              Package {{ pkg.name }} purchased successfully
                            </div>
                          </div>
                          <div v-else>
                            <DialogHeader class="flex flex-col items-start">
                              <DialogTitle class="text-red flex gap-3 items-center justify-center sm:justify-start">
                                <div class="i-material-symbols-error-outline-rounded text-3xl" />
                                Error
                              </DialogTitle>
                              <DialogDescription>
                                An error occured
                              </DialogDescription>
                            </DialogHeader>
                            <div class="font-semibold py-2">
                              {{ errMessage }}
                            </div>
                            <DialogFooter>
                              <Button variant="default" class="w-full" @click="retry">
                                Try Again
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </template>
                      <template v-if="pkg.planType === 'FREE TRIAL'">
                        <div v-if="processing">
                          <DialogHeader class="flex flex-col items-start">
                            <div class="text-2xl font-400 text-red">
                              Do not reload this page
                            </div>
                            <DialogTitle class="text-orange flex gap-3 items-center justify-center sm:justify-start">
                              <div class="i-eos-icons-loading text-3xl" />
                              Connecting
                            </DialogTitle>
                            <DialogDescription>
                              Wait a moment as we connect you to the internet
                            </DialogDescription>
                          </DialogHeader>
                        </div>
                        <div v-else>
                          <div v-if="success">
                            <DialogHeader class="flex flex-col items-start">
                              <DialogTitle class="text-green flex gap-3 items-center justify-center sm:justify-start">
                                <div class="i-clarity-success-standard-line text-3xl" />
                                Success
                              </DialogTitle>
                              <DialogDescription>
                                Successfully connected
                              </DialogDescription>
                            </DialogHeader>
                            <div class="font-semibold py-2">
                              Package {{ pkg.name }} purchased successfully
                            </div>
                          </div>
                          <div v-else>
                            <DialogHeader class="flex flex-col items-start">
                              <DialogTitle class="text-red flex gap-3 items-center justify-center sm:justify-start">
                                <div class="i-material-symbols-error-outline-rounded text-3xl" />
                                Error
                              </DialogTitle>
                              <DialogDescription>
                                An error occured
                              </DialogDescription>
                            </DialogHeader>
                            <div class="font-semibold py-2">
                              {{ errMessage }}
                            </div>
                            <DialogFooter>
                              <Button variant="default" class="w-full" @click="retry">
                                Try Again
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </template>
                      <template v-if="pkg.planType === 'AD'">
                        <div v-if="processing">
                          <DialogHeader class="flex flex-col items-start">
                            <div class="text-2xl font-400 text-red">
                              Do not reload this page
                            </div>
                            <DialogTitle class="text-orange flex gap-3 items-center justify-center sm:justify-start">
                              <div class="i-eos-icons-loading text-3xl" />
                              Connecting
                            </DialogTitle>
                            <DialogDescription>
                              Wait a moment as we connect you to the internet
                            </DialogDescription>
                          </DialogHeader>
                        </div>
                        <div v-else>
                          <div v-if="success">
                            <DialogHeader class="flex flex-col items-start">
                              <DialogTitle class="text-green flex gap-3 items-center justify-center sm:justify-start">
                                <div class="i-clarity-success-standard-line text-3xl" />
                                Success
                              </DialogTitle>
                              <DialogDescription>
                                Successfully connected
                              </DialogDescription>
                            </DialogHeader>
                            <div class="font-semibold py-2">
                              Package {{ pkg.name }} purchased successfully
                            </div>
                          </div>
                          <div v-else>
                            <DialogHeader class="flex flex-col items-start">
                              <DialogTitle class="text-red flex gap-3 items-center justify-center sm:justify-start">
                                <div class="i-material-symbols-error-outline-rounded text-3xl" />
                                Error
                              </DialogTitle>
                              <DialogDescription>
                                An error occured
                              </DialogDescription>
                            </DialogHeader>
                            <div class="font-semibold py-2">
                              {{ errMessage }}
                            </div>
                            <DialogFooter>
                              <Button variant="default" class="w-full" @click="retry">
                                Try Again
                              </Button>
                            </DialogFooter>
                          </div>
                        </div>
                      </template>
                    </DialogContent>
                  </PackageCard>
                </template>
              </div>
            </TabsContent>
            <TabsContent value="voucher">
              <!-- Voucher -->
              <div class="relative w-full max-h-full my-100px p-1 z-10">
                <Card class="relative flex flex-col justify-between bg-background/70 rounded-xl p-4 border border-border transition-all duration-300 mt-2 hover:shadow-md hover:border-primary/20 max-w-500px min-w-100px mt-2 mx-auto bg-background/70">
                  <CardHeader>
                    <CardTitle>Voucher</CardTitle>
                    <CardDescription>
                      Connect to the internet using a voucher.
                    </CardDescription>
                  </CardHeader>
                  <form @submit="handleVoucherSubmit">
                    <CardContent class="space-y-2">
                      <Label for="voucher" class="space-y-1">
                        Enter Voucher
                        <Input id="voucher" v-model="voucherCode" type="text" class="uppercase" />
                      </Label>
                    </CardContent>
                    <CardFooter>
                      <Button type="submit" class="w-full" :disabled="voucherProcessing">
                        {{ voucherProcessing ? 'Connecting...' : 'Connect' }}
                      </Button>
                    </CardFooter>
                  </form>
                </Card>
              </div>

              <!-- Voucher Processing Dialog -->
              <Dialog :open="voucherProcessing || voucherSuccess || voucherError" @update:open="!$event && resetVoucherState()">
                <DialogContent
                  class="sm:max-w-[425px]"
                  @interact-outside.prevent
                  @focus-outside.prevent
                  @pointer-down-outside.prevent
                >
                  <div v-if="voucherProcessing">
                    <DialogHeader class="flex flex-col items-start">
                      <div class="text-2xl font-400 text-red">
                        Do not reload this page
                      </div>
                      <DialogTitle class="text-orange flex gap-3 items-center justify-center sm:justify-start">
                        <div class="i-eos-icons-loading text-3xl" />
                        Connecting
                      </DialogTitle>
                      <DialogDescription>
                        Validating voucher and connecting you to the internet
                      </DialogDescription>
                    </DialogHeader>
                  </div>
                  <div v-else-if="voucherSuccess">
                    <DialogHeader class="flex flex-col items-start">
                      <DialogTitle class="text-green flex gap-3 items-center justify-center sm:justify-start">
                        <div class="i-clarity-success-standard-line text-3xl" />
                        Success
                      </DialogTitle>
                      <DialogDescription>
                        Successfully connected
                      </DialogDescription>
                    </DialogHeader>
                    <div class="font-semibold py-2">
                      Your voucher has been activated
                    </div>
                  </div>
                  <div v-else-if="voucherError">
                    <DialogHeader class="flex flex-col items-start">
                      <DialogTitle class="text-red flex gap-3 items-center justify-center sm:justify-start">
                        <div class="i-material-symbols-error-outline-rounded text-3xl" />
                        Error
                      </DialogTitle>
                      <DialogDescription>
                        An error occurred
                      </DialogDescription>
                    </DialogHeader>
                    <div class="font-semibold py-2">
                      {{ voucherErrorMessage }}
                    </div>
                    <DialogFooter>
                      <Button variant="default" class="w-full" @click="resetVoucherState">
                        Try Again
                      </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          </Tabs>
        </div>
        <!-- footer -->
        <div class="w-full rounded-0 bg-muted p-4 flex items-center justify-center z-1 gap-2 fixed bottom-0 left-0">
          <a href="https://wezeshanet.com/terms" target="_blank" rel="noopener" class="link">Terms of Service</a> and <a href="https://wezeshanet.com/privacy-policy" target="_blank" rel="noopener" class="link">Privacy Policy</a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type=number] {
  -moz-appearance: textfield;
  appearance: textfield;
}

input:focus::placeholder {
  color: transparent;
}

.link {
  text-decoration: underline;
  color: rgb(43, 116, 226);
}

.link:hover {
  text-decoration: underline;
  text-decoration-style: dotted;
}
</style>
